import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Chip, Divider, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassButton, LoadingOverlay } from 'dashboard/components/common';

/**
 * How this brand's customers sign in.
 *
 * This was its own screen in the left menu, which put it a long way from the
 * switch that decides whether customers can sign in at all, and from the
 * header the sign-in link appears in. It is a section of the site's global
 * design now, next to both.
 *
 * The thing this screen has to be honest about is whose sign-in button the
 * customer actually sees. A brand that has supplied its own Google
 * credentials gets a consent screen with its own name on it. A brand that
 * has not gets a working button whose consent screen says TechProOne — which
 * is a perfectly reasonable default and a genuinely unwelcome surprise if
 * you learn it from a customer rather than from here.
 *
 * So each provider says, in words, which it is doing right now.
 */

const PROVIDERS = [
  {
    key: 'google',
    label: 'Google',
    help: 'Google Cloud Console → APIs & Services → Credentials → OAuth client ID (Web).',
  },
  {
    key: 'facebook',
    label: 'Facebook',
    help: 'Meta for Developers → your app → Settings → Basic.',
  },
];

export default function SignInProviders({ brandId: selectedBrand }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) { setLoading(false); return; }
    setLoading(true);
    API.getBrandAuthConfig({ brand_id: selectedBrand })
      .then((res) => {
        setConfig(res);
        // Secrets are never sent back, so the boxes start empty and an empty
        // box means "leave it as it is" — see the note on saving below.
        setForm({
          google: { ...res.providers.google, client_secret: '' },
          facebook: { ...res.providers.facebook, client_secret: '' },
          allow_platform_fallback: res.allow_platform_fallback,
        });
      })
      .catch((err) => toast.error(err?.message || 'Could not load sign-in settings'))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => { load(); }, [load]);

  const setProvider = (key, patch) =>
    setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const save = () => {
    setSaving(true);
    API.updateBrandAuthConfig({ brand_id: selectedBrand, patch: form })
      .then((res) => {
        setConfig(res);
        // Clear the secret boxes again: what is stored cannot be shown, and
        // leaving the typed value on screen suggests it can.
        setForm((f) => ({
          ...f,
          google: { ...f.google, client_secret: '' },
          facebook: { ...f.facebook, client_secret: '' },
        }));
        toast.success('Saved');
      })
      .catch((err) => toast.error(err?.message || 'Could not save'))
      .finally(() => setSaving(false));
  };

  const effectiveFor = (key) => (config?.effective || []).find((e) => e.provider === key);

  if (!selectedBrand) return null;

  return (
    <Card>
      <CardContent sx={{ p: '28px', '&:last-child': { pb: '28px' } }}>
        <Stack direction='row' alignItems='flex-start' justifyContent='space-between' gap='16px' mb='18px'>
          <Box>
            <Typography variant='h6' fontWeight='bold'>Sign-in buttons</Typography>
            <Typography variant='body2' color='text.secondary'>
              How people who buy from you log in, and whose name they see when they do.
            </Typography>
          </Box>
          <GlassButton
            variant='contained' onClick={save}
            disabled={saving}
            disabledReason='Saving…'
          >
            Save
          </GlassButton>
        </Stack>

        <LoadingOverlay loading={loading} minHeight={220}>
            <Stack spacing='22px'>
              {PROVIDERS.map(({ key, label, help }) => {
                const effective = effectiveFor(key);
                const mine = effective?.owner === 'brand';
                return (
                  <Box key={key}>
                    <Stack direction='row' alignItems='center' gap='10px' flexWrap='wrap' mb='4px'>
                      <Typography fontWeight='700'>{label}</Typography>
                      {effective?.available ? (
                        <Chip
                          size='small'
                          color={mine ? 'success' : 'default'}
                          label={mine ? 'Your own app' : 'Using ours'}
                        />
                      ) : (
                        <Chip size='small' variant='outlined' label='Off' />
                      )}
                    </Stack>

                    {/* The sentence that stops this being a surprise. */}
                    <Typography variant='caption' color='text.secondary' display='block' mb='10px'>
                      {!effective?.available
                        ? `Customers cannot sign in with ${label} at the moment.`
                        : mine
                          ? `Customers see your own name on the ${label} screen.`
                          : `Customers see “TechProOne” on the ${label} screen. Add your own `
                            + 'credentials below to show your name instead.'}
                    </Typography>

                    <FormControlLabel
                      control={(
                        <Switch
                          checked={Boolean(form[key]?.enabled)}
                          onChange={(e) => setProvider(key, { enabled: e.target.checked })}
                        />
                      )}
                      label={`Use my own ${label} app`}
                    />

                    {form[key]?.enabled && (
                      <Stack spacing='10px' mt='8px'>
                        <Typography variant='caption' color='text.secondary'>{help}</Typography>
                        <TextField
                          size='small' label='Client ID'
                          value={form[key]?.client_id || ''}
                          onChange={(e) => setProvider(key, { client_id: e.target.value })}
                        />
                        <TextField
                          size='small' label='Client secret' type='password'
                          value={form[key]?.client_secret || ''}
                          onChange={(e) => setProvider(key, { client_secret: e.target.value })}
                          placeholder={config?.providers?.[key]?.has_secret ? '•••••••• (already set)' : ''}
                          // Saved secrets cannot be read back, so an empty box
                          // means "keep what is stored". Without saying so, the
                          // obvious reading is that saving wipes it.
                          helperText={config?.providers?.[key]?.has_secret
                            ? 'Leave blank to keep the one already saved.'
                            : 'Stored encrypted. It cannot be read back afterwards.'}
                        />
                        <TextField
                          size='small' label='Redirect URI'
                          value={form[key]?.redirect_uri || ''}
                          onChange={(e) => setProvider(key, { redirect_uri: e.target.value })}
                          // The most common failure in this whole area is a
                          // redirect that differs by one character.
                          helperText='Must match the provider’s console exactly, including the scheme and any trailing slash.'
                        />
                      </Stack>
                    )}
                    <Divider sx={{ mt: '18px' }} />
                  </Box>
                );
              })}

              <FormControlLabel
                control={(
                  <Switch
                    checked={form.allow_platform_fallback !== false}
                    onChange={(e) => setForm((f) => ({ ...f, allow_platform_fallback: e.target.checked }))}
                  />
                )}
                label='Keep social sign-in working using our app when mine is not set up'
              />
              <Typography variant='caption' color='text.secondary' mt='-10px'>
                Switch this off and customers will only see the sign-in methods you have
                configured yourself. Everything else falls back to email and password.
              </Typography>
            </Stack>
        </LoadingOverlay>
      </CardContent>
    </Card>
  );
}
